import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useInView } from "framer-motion";
import { MaskLine, FadeUp } from "./Reveal";
import Footer from "./Footer";

const MEDIA = "/media/projects/hot-ones-game-night";

const BOARDS = [
    { title: "Reaper Ridge", src: `${MEDIA}/board-reaper-ridge.mp4`, poster: `${MEDIA}/board-reaper-ridge-poster.jpg` },
    { title: "Habanero Heights", src: `${MEDIA}/board-habenero-heights.mp4`, poster: `${MEDIA}/board-habenero-heights-poster.jpg` },
    { title: "Pepper Playground", src: `${MEDIA}/board-pepper-playground.mp4`, poster: `${MEDIA}/board-pepper-playground-poster.jpg` },
    { title: "Sriracha Swamp", src: `${MEDIA}/board-sriracha-swamp.mp4`, poster: `${MEDIA}/board-sriracha-swamp-poster.jpg` },
];

const STILLS = [1, 2, 3, 4].map((n) => ({
    src: `${MEDIA}/promo-still-${n}.webp`,
    label: `Promo still — 0${n}`,
}));

const SERVICES = [
    "Logo design",
    "Visual identity",
    "Art direction",
    "Graphics package",
    "Board design",
    "Game assets",
    "3D",
    "Motion design",
    "Key art",
    "YouTube thumbnail",
];

function AutoVideo({ src, poster, label, testid }) {
    const wrapRef = useRef(null);
    const videoRef = useRef(null);
    const inView = useInView(wrapRef, { amount: 0.3 });
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        if (inView) {
            v.play().catch(() => {});
        } else {
            v.pause();
        }
    }, [inView]);
    return (
        <figure
            ref={wrapRef}
            data-testid={testid}
            className="relative aspect-video overflow-hidden border border-white/15 bg-black"
        >
            <video
                ref={videoRef}
                className="h-full w-full object-cover"
                src={src}
                poster={poster}
                muted
                loop
                playsInline
                preload="none"
            />
            <figcaption className="absolute left-3 top-3 border border-white/25 bg-black/60 px-2 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-white/70">
                {label}
            </figcaption>
        </figure>
    );
}

function SubLabel({ children }) {
    return (
        <FadeUp className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/40 md:text-xs">
            <span className="text-mmm-red">///</span> {children}
        </FadeUp>
    );
}

function Passage({ children, testid }) {
    return (
        <FadeUp className="max-w-[62ch] text-base leading-relaxed text-white/70 md:text-lg">
            <p data-testid={testid}>{children}</p>
        </FadeUp>
    );
}

function Caption({ children }) {
    return (
        <figcaption className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
            {children}
        </figcaption>
    );
}

function SmallAsset({ src, label, testid }) {
    return (
        <FadeUp>
            <figure
                data-testid={testid}
                className="flex aspect-square items-center justify-center border border-white/15 bg-black p-6 md:p-8"
            >
                <img
                    src={src}
                    alt={label}
                    loading="lazy"
                    className="max-h-[220px] w-auto max-w-full object-contain md:max-h-[260px]"
                />
            </figure>
            <Caption>{label}</Caption>
        </FadeUp>
    );
}

export default function CaseStudy() {
    useEffect(() => {
        document.title = "Hot Ones: Game Night — Money Mac Media®";
        return () => {
            document.title = "MONEY MAC MEDIA® — Creative Capital At Any Cost™";
        };
    }, []);

    return (
        <div className="bg-black text-white">
            {/* 1 — HERO: KEY ART */}
            <section className="px-5 pb-16 pt-28 md:px-8 md:pb-24 md:pt-40">
                <FadeUp>
                    <Link
                        to="/"
                        data-testid="case-study-back-link"
                        className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/50 transition-colors duration-300 hover:text-mmm-red md:text-xs"
                    >
                        ← Money Mac Media
                    </Link>
                </FadeUp>

                <div className="mt-8 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em] md:text-xs">
                    <FadeUp>
                        <span className="text-mmm-red">001</span> — Latest Project
                    </FadeUp>
                    <FadeUp className="hidden text-white/40 md:block">The money shot</FadeUp>
                </div>

                <h1 className="mt-10 font-display text-[clamp(2.8rem,8.5vw,8.5rem)] uppercase leading-[0.88] md:mt-14">
                    <MaskLine delay={0.05}>First we feast:</MaskLine>
                    <MaskLine delay={0.15}>
                        <span className="text-mmm-red">Hot Ones Game Night</span>
                    </MaskLine>
                </h1>

                <FadeUp delay={0.1} className="mt-10 md:mt-14">
                    <p className="max-w-3xl font-display text-2xl uppercase leading-tight md:text-4xl">
                        A game show needs more than graphics. It needs a world.
                    </p>
                </FadeUp>

                <Passage testid="case-study-description">
                    Money Mac Media developed the visual identity for Hot Ones: Game
                    Night, beginning with the logo and graphics package and expanding
                    across the physical board game, on-screen graphics, key art, and
                    promotional creative.
                </Passage>

                <FadeUp className="mt-14 flex justify-center md:mt-20">
                    <figure
                        data-testid="case-study-keyart"
                        className="border border-white/15 bg-black p-4 md:p-10"
                    >
                        <img
                            src={`${MEDIA}/keyart.webp`}
                            alt="Hot Ones Game Night key art"
                            className="mx-auto h-auto w-auto max-h-[72vh]"
                        />
                        <figcaption className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.22em] text-white/40">
                            Key art — Hot Ones: Game Night
                        </figcaption>
                    </figure>
                </FadeUp>
            </section>

            {/* 2 — TITLE DESIGN / GRAPHICS PACKAGE */}
            <section className="px-5 pb-20 md:px-8 md:pb-28">
                <SubLabel>Title design — graphics package</SubLabel>
                <Passage testid="case-study-title-copy">
                    <span className="mt-6 block md:mt-8" />
                    First We Feast approached Money Mac Media to create the logo and
                    graphics package for a new Hot Ones show built around a physical
                    board game. The visual direction drew from the escalating journey
                    toward Mount Scoville — heat, lava, volcanic terrain, and the game’s
                    color system establishing the identity.
                </Passage>
                <FadeUp className="mt-10">
                    <AutoVideo
                        src={`${MEDIA}/intro.mp4`}
                        poster={`${MEDIA}/intro-poster.jpg`}
                        label="Title treatment — animated"
                        testid="case-study-hero-video"
                    />
                </FadeUp>
                <FadeUp className="mt-6">
                    <figure data-testid="case-study-title-card">
                        <img
                            src={`${MEDIA}/title-card.webp`}
                            alt="Hot Ones Game Night logo title card"
                            loading="lazy"
                            className="w-full border border-white/15 bg-black object-contain"
                        />
                        <Caption>Logo — title card</Caption>
                    </figure>
                </FadeUp>
            </section>

            {/* 3 — THE PROJECT EXPANDS: BOARD DESIGN */}
            <section className="px-5 pb-20 md:px-8 md:pb-28">
                <SubLabel>The project expands — board design</SubLabel>
                <Passage testid="case-study-board-copy">
                    After the show’s initial visual identity, the scope expanded to
                    include the design of the physical game board and its supporting
                    visual assets. The board translates the game’s progression into a
                    continuous illustrated journey — each region with its own
                    environment, characters, landmarks, and personality, all part of the
                    same visual world. The established gameplay and progression informed
                    the design; the rules of the game were not ours to make.
                </Passage>
                <FadeUp className="mt-10">
                    <figure data-testid="case-study-board-full">
                        <img
                            src={`${MEDIA}/board-design.webp`}
                            alt="Hot Ones Game Night full game board — Jalapeño Junction to Mount Scoville"
                            loading="lazy"
                            className="w-full border border-white/15 bg-black object-contain"
                        />
                        <Caption>The full board — Jalapeño Junction to Mount Scoville</Caption>
                    </figure>
                </FadeUp>
                <FadeUp className="mt-14 md:mt-20">
                    <SubLabel>The board worlds</SubLabel>
                </FadeUp>
                <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                    {BOARDS.map((b) => (
                        <FadeUp key={b.title}>
                            <AutoVideo
                                src={b.src}
                                poster={b.poster}
                                label={b.title}
                                testid={`case-study-board-${b.title.toLowerCase().replace(/ /g, "-")}`}
                            />
                        </FadeUp>
                    ))}
                </div>
            </section>

            {/* 4 — BOARD GAME ASSETS */}
            <section className="px-5 pb-20 md:px-8 md:pb-28">
                <SubLabel>Board game assets</SubLabel>
                <Passage testid="case-study-assets-copy">
                    Character pieces, the volcano / Last Dab holder, icons, signage,
                    characters, hazards, and dice — the physical extensions of the
                    identity, built to the same world as the board.
                </Passage>
                <FadeUp className="mt-10">
                    <figure data-testid="case-study-assets-tokens">
                        <img
                            src={`${MEDIA}/metal-tokens.webp`}
                            alt="Hot Ones Game Night character and player pieces — metal tokens"
                            loading="lazy"
                            className="w-full border border-white/15 bg-black object-contain"
                        />
                        <Caption>Character & player pieces — metal tokens</Caption>
                    </figure>
                </FadeUp>
                <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-12">
                    <FadeUp className="md:col-span-7">
                        <figure
                            data-testid="case-study-assets-volcano"
                            className="flex items-center justify-center border border-white/15 bg-black p-8 md:p-14"
                        >
                            <img
                                src={`${MEDIA}/volcano-holder-1.webp`}
                                alt="Volcano holder for the Last Dab — physical game asset"
                                loading="lazy"
                                className="max-h-[420px] w-auto object-contain md:max-h-[520px]"
                            />
                        </figure>
                        <Caption>Volcano holder — where the Last Dab lives</Caption>
                    </FadeUp>
                    <div className="grid grid-cols-2 gap-4 md:col-span-5">
                        <SmallAsset
                            src={`${MEDIA}/ghost-pepper.webp`}
                            label="Ghost pepper — board asset"
                            testid="case-study-assets-ghost"
                        />
                        <SmallAsset
                            src={`${MEDIA}/volcano-holder-2.webp`}
                            label="Volcano holder — 02"
                            testid="case-study-assets-volcano-2"
                        />
                    </div>
                </div>
            </section>

            {/* 5 — ON-SCREEN GAME GRAPHICS */}
            <section className="px-5 pb-20 md:px-8 md:pb-28">
                <SubLabel>On-screen game graphics</SubLabel>
                <Passage testid="case-study-onscreen-copy">
                    The board’s visual language, translated into on-screen game graphics
                    that help communicate gameplay to the audience.
                </Passage>
                <FadeUp className="mt-10">
                    <figure data-testid="case-study-onscreen-gfx">
                        <img
                            src={`${MEDIA}/gfx-package.webp`}
                            alt="Hot Ones Game Night on-screen graphics package"
                            loading="lazy"
                            className="w-full border border-white/15 bg-black object-contain"
                        />
                        <Caption>On-screen graphics package</Caption>
                    </figure>
                </FadeUp>
            </section>

            {/* 6 — PROMOTIONAL APPLICATIONS */}
            <section className="px-5 pb-20 md:px-8 md:pb-28">
                <SubLabel>Promotional applications — key art & thumbnail</SubLabel>
                <Passage testid="case-study-promo-copy">
                    The identity extended into promotional creative — hosts, board,
                    Mount Scoville, dice, game pieces, fire, and the format’s
                    recognizable elements, brought together for campaign work.
                </Passage>
                <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-12">
                    <FadeUp className="md:col-span-8">
                        <figure data-testid="case-study-thumbnail">
                            <img
                                src={`${MEDIA}/thumbnail.webp`}
                                alt="Hot Ones Game Night YouTube thumbnail"
                                loading="lazy"
                                className="w-full border border-white/15 bg-black object-contain"
                            />
                            <Caption>YouTube thumbnail</Caption>
                        </figure>
                    </FadeUp>
                    <FadeUp delay={0.1} className="md:col-span-4">
                        <figure
                            data-testid="case-study-keyart-crop"
                            className="aspect-[3/4] overflow-hidden border border-white/15 bg-black"
                        >
                            <img
                                src={`${MEDIA}/keyart.webp`}
                                alt="Hot Ones Game Night key art — campaign crop"
                                loading="lazy"
                                className="h-full w-full scale-[1.35] object-cover object-top"
                            />
                        </figure>
                        <Caption>Key art — campaign crop</Caption>
                    </FadeUp>
                </div>
            </section>

            {/* 7 — IN THE WILD */}
            <section className="px-5 pb-20 md:px-8 md:pb-28">
                <h2 className="font-display text-[clamp(2.4rem,6.5vw,6.5rem)] uppercase leading-[0.9]">
                    <MaskLine delay={0.05}>
                        In the <span className="text-mmm-red">wild.</span>
                    </MaskLine>
                </h2>
                <Passage testid="case-study-wild-copy">
                    From the physical board to the graphics on screen, the visual system
                    carries through the finished show.
                </Passage>
                <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2">
                    {STILLS.map((s) => (
                        <FadeUp key={s.label}>
                            <figure
                                data-testid={`case-still-${s.label.slice(-2)}`}
                                className="flex aspect-video items-center justify-center overflow-hidden border border-white/15 bg-black"
                            >
                                <img
                                    src={s.src}
                                    alt={s.label}
                                    loading="lazy"
                                    className="max-h-full max-w-full object-contain"
                                />
                            </figure>
                        </FadeUp>
                    ))}
                </div>
            </section>

            {/* 8 — SERVICES */}
            <section className="px-5 pb-20 md:px-8 md:pb-28">
                <FadeUp>
                    <p
                        data-testid="case-study-services"
                        className="max-w-4xl font-mono text-[10px] uppercase leading-loose tracking-[0.2em] text-white/50 md:text-xs"
                    >
                        {SERVICES.map((s, i) => (
                            <span key={s}>
                                {s}
                                {i < SERVICES.length - 1 && (
                                    <span className="text-mmm-red"> · </span>
                                )}
                            </span>
                        ))}
                    </p>
                </FadeUp>
            </section>

            {/* NEW BUSINESS */}
            <section className="bg-white px-5 py-20 text-black md:px-8 md:py-28">
                <FadeUp className="font-mono text-[10px] uppercase tracking-[0.22em] md:text-xs">
                    <span className="text-mmm-red">004</span> — New Business
                </FadeUp>
                <FadeUp delay={0.1} className="mt-8 max-w-2xl text-base leading-relaxed text-black/60 md:text-lg">
                    <p>
                        Want a universe like this for your show, campaign, or strange
                        little idea? Tell us what you’re making.
                    </p>
                </FadeUp>
                <FadeUp delay={0.15} className="mt-10">
                    <a
                        data-testid="case-study-cta"
                        href="mailto:hello@moneymacmedia.com"
                        className="inline-block border-b-[6px] border-black pb-2 font-display text-[clamp(1.8rem,5vw,5rem)] uppercase leading-none transition-colors duration-300 hover:border-mmm-red hover:text-mmm-red"
                    >
                        Let’s talk business →
                    </a>
                </FadeUp>
                <FadeUp delay={0.2} className="mt-8">
                    <a
                        data-testid="case-study-email-link"
                        href="mailto:hello@moneymacmedia.com"
                        className="font-mono text-xs uppercase tracking-[0.2em] text-black/50 underline-offset-4 transition-colors duration-300 hover:text-mmm-red hover:underline md:text-sm"
                    >
                        hello@moneymacmedia.com
                    </a>
                </FadeUp>
            </section>

            <Footer />
        </div>
    );
}
